<template>
  <v-container fluid class="ps-barcode-page">
    <KpiCards class="mb-3" :items="kpiItems" />

    <!-- Main Table / Grid Card -->
    <v-card class="ps-card-soft ps-table-card">
      <v-card-text>
        <!-- Table Head -->
        <div class="d-flex align-center flex-wrap ps-table-head">
          <span class="ps-title-tick"></span>
          <h1 class="ps-table-title">{{ $t("manage_barcode") || "Manage Barcode" }}</h1>
          <span v-if="product_barcode.total" class="ps-count-note">
            {{ paginationStart }} - {{ paginationEnd }} {{ $t("of") || "of" }} {{ product_barcode.total }}
          </span>

          <v-spacer />

          <!-- View Toggle (Grid / List) -->
          <v-btn-toggle
            v-model="viewMode"
            mandatory
            dense
            class="ps-status-toggle"
          >
            <v-btn small value="grid">
              <v-icon small class="mr-1">fal fa-th-large</v-icon>
              {{ $t("grid") || "Grid" }}
            </v-btn>
            <v-btn small value="list">
              <v-icon small class="mr-1">fal fa-list</v-icon>
              {{ $t("list") || "List" }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Filter Bar -->
        <div class="d-flex align-center flex-wrap ps-filter-bar">
          <div class="ps-search-wrap">
            <TextSearch
              v-model="search"
              @search="fetchProducts"
              @onClear="
                search = '';
                fetchProducts();
              "
            />
          </div>

          <v-checkbox
            :input-value="allVisibleSelected"
            :indeterminate="someVisibleSelected && !allVisibleSelected"
            color="primary"
            dense
            hide-details
            :label="$t('select_all_visible') || 'Select all visible'"
            class="ma-0 pa-0 ps-select-all-cb"
            @change="toggleSelectAllVisible"
          />

          <v-spacer />

          <!-- Batch Print Button -->
          <v-btn
            color="primary"
            depressed
            height="38"
            class="ps-batch-print-btn"
            :disabled="selectedProducts.length === 0"
            @click="openBatchPrintModal"
          >
            <v-icon left small>fal fa-print</v-icon>
            {{ $t("print_selected_barcodes") || "Print Selected (A4)" }}
            <span v-if="selectedProducts.length > 0" class="ps-print-badge ml-2">
              {{ selectedProducts.length }}
            </span>
          </v-btn>
        </div>

        <!-- Selection Banner (appears when items are selected) -->
        <v-slide-y-transition>
          <div
            v-if="selectedProducts.length > 0"
            class="ps-selection-banner"
          >
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">fal fa-check-circle</v-icon>
              <span class="ps-selection-banner-text">
                {{ $t("selected_items_count", { count: selectedProducts.length }) }}
              </span>
            </div>
            <div class="d-flex align-center" style="gap: 8px">
              <v-btn
                text
                small
                color="error"
                class="ps-banner-clear-btn"
                @click="clearSelection"
              >
                <v-icon left small>fal fa-times</v-icon>
                {{ $t("clear_selection") || "Clear" }}
              </v-btn>
              <v-btn
                color="primary"
                small
                depressed
                class="ps-banner-print-btn"
                @click="openBatchPrintModal"
              >
                <v-icon left small>fal fa-print</v-icon>
                {{ $t("print_selected_barcodes") || "Print Selected (A4)" }}
              </v-btn>
            </div>
          </div>
        </v-slide-y-transition>

        <!-- Loading State -->
        <div v-if="loading" class="py-12 d-flex justify-center">
          <v-progress-circular indeterminate size="48" color="primary" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!products.length"
          class="ps-empty py-12"
        >
          <v-icon class="ps-empty-icon mb-3" size="48" color="grey lighten-1">fal fa-box-open</v-icon>
          <span class="ps-empty-text text-body-1 font-weight-medium grey--text text--darken-1 mb-2">
            {{ $t("no_products_found") || "No products found" }}
          </span>
          <span class="text-caption grey--text mb-3">
            {{ $t("try_different_search") || "Try a different search term" }}
          </span>
          <v-btn
            v-if="search"
            text
            small
            color="primary"
            @click="
              search = '';
              fetchProducts();
            "
          >
            {{ $t("clear_filters") || "Clear search" }}
          </v-btn>
        </div>

        <!-- Grid View -->
        <div
          v-else-if="viewMode === 'grid'"
          class="ps-barcode-grid"
        >
          <div
            v-for="product in products"
            :key="product.code"
            class="ps-barcode-card"
            :class="{ 'is-selected': isSelected(product) }"
            @click="toggleSelect(product)"
          >
            <!-- Checkbox top-left (color=primary) -->
            <v-checkbox
              :input-value="isSelected(product)"
              color="primary"
              class="ps-card-checkbox ma-0 pa-0"
              hide-details
              dense
              @click.stop
              @change="toggleSelect(product)"
            />

            <!-- Top Image -->
            <div class="ps-card-image-wrap">
              <div class="ps-card-image">
                <app-img :img="product.img" height="100%" width="100%" />
              </div>
              <span v-if="!product.is_active" class="ps-card-status-chip">
                {{ $t("inactive") || "Inactive" }}
              </span>
              <div class="ps-card-overlay" @click.stop="openBarcodeModal(product)">
                <v-btn fab small depressed color="primary" class="ps-overlay-btn">
                  <v-icon color="white">fal fa-barcode</v-icon>
                </v-btn>
                <span class="ps-overlay-text">{{ $t("view_barcode") || "View Barcode" }}</span>
              </div>
            </div>

            <!-- Card Body -->
            <div class="ps-card-body">
              <div class="ps-card-name" :title="product.name">{{ product.name }}</div>
              <div class="ps-card-code">
                <v-icon x-small class="mr-1">fal fa-barcode</v-icon>
                <span>{{ product.code }}</span>
                <button
                  type="button"
                  class="ps-copy-code"
                  :title="$t('copy')"
                  @click.stop="copyBarcode(product.code)"
                >
                  <v-icon x-small>fal fa-copy</v-icon>
                </button>
              </div>

              <div class="ps-card-meta">
                <span v-if="product.category" class="ps-category-pill">
                  {{ product.category.la_name || product.category.en_name }}
                </span>
                <span class="ps-unit-pill">
                  {{ getUnitDisplay(product) }}
                </span>
              </div>

              <div class="d-flex align-center justify-space-between ps-card-footer">
                <div class="ps-card-price">
                  {{ priceWithCurrency(product.price_sale, product.currency) }}
                </div>
                <v-btn
                  icon
                  small
                  class="ps-card-action-icon"
                  :title="$t('view_barcode') || 'View Barcode'"
                  @click.stop="openBarcodeModal(product)"
                >
                  <v-icon small color="primary">fal fa-barcode</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- List View (Table) -->
        <div v-else-if="viewMode === 'list'" class="ps-table-wrap">
          <v-data-table
            :headers="tableHeaders"
            :items="products"
            :loading="loading"
            :options.sync="tableOptions"
            :server-items-length="product_barcode.total || 0"
            show-select
            item-key="code"
            v-model="selectedProducts"
            hide-default-footer
            class="ps-data-table elevation-0"
          >
            <template v-slot:item.img="{ item }">
              <v-avatar tile size="36" class="rounded-lg ps-thumb-avatar">
                <app-img :img="item.img" />
              </v-avatar>
            </template>

            <template v-slot:item.code="{ item }">
              <span class="ps-row-code">
                {{ item.code }}
                <button
                  type="button"
                  class="ps-copy-code ml-1"
                  :title="$t('copy')"
                  @click.stop="copyBarcode(item.code)"
                >
                  <v-icon x-small>fal fa-copy</v-icon>
                </button>
              </span>
            </template>

            <template v-slot:item.name="{ item }">
              <span class="ps-product-name font-weight-bold">{{ item.name }}</span>
            </template>

            <template v-slot:item.category.la_name="{ item }">
              <span v-if="item.category" class="ps-category-pill">
                {{ item.category.la_name || item.category.en_name }}
              </span>
              <span v-else class="text-caption grey--text">-</span>
            </template>

            <template v-slot:item.size.name="{ item }">
              <span class="ps-unit-pill">
                {{ getUnitDisplay(item) }}
              </span>
            </template>

            <template v-slot:item.price_sale="{ item }">
              <span class="ps-price-cell font-weight-bold">
                {{ priceWithCurrency(item.price_sale, item.currency) }}
              </span>
            </template>

            <template v-slot:item.stock="{ item }">
              <span class="font-weight-medium">{{ item.stock }}</span>
            </template>

            <template v-slot:item.is_active="{ item }">
              <span
                class="ps-status-chip"
                :class="item.is_active ? 'is-active' : 'is-inactive'"
              >
                {{ item.is_active ? ($t("active") || "Active") : ($t("inactive") || "Inactive") }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="ps-row-action-btn"
                    @click.stop="openBarcodeModal(item)"
                  >
                    <v-icon small color="primary">fal fa-barcode</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("view_barcode") || "View Barcode" }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </div>

        <!-- Pagination (unified for both Grid & List view) -->
        <div v-if="products.length > 0" class="d-flex align-center flex-wrap ps-pagination pt-4">
          <span class="ps-page-note">
            {{ paginationStart }} - {{ paginationEnd }} {{ $t("of") || "of" }} {{ product_barcode.total || 0 }}
          </span>

          <v-spacer />

          <div class="d-flex align-center ps-per-page">
            <span>{{ $t("rows_per_page") || "Rows per page" }}</span>
            <div class="ps-per-page-select">
              <select v-model.number="currentItemsPerPage">
                <option v-for="n in rowsPerPageItems" :key="n" :value="n">
                  {{ n }}
                </option>
              </select>
            </div>
          </div>

          <div class="d-flex align-center ps-pager">
            <span
              class="ps-pager-btn"
              :class="{ 'is-disabled': currentPage <= 1 }"
              @click="prevPage"
            >
              <v-icon x-small>fal fa-chevron-left</v-icon>
            </span>
            <span class="ps-pager-info">
              {{ currentPage }} / {{ totalPages || 1 }}
            </span>
            <span
              class="ps-pager-btn"
              :class="{ 'is-disabled': currentPage >= totalPages }"
              @click="nextPage"
            >
              <v-icon x-small>fal fa-chevron-right</v-icon>
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Modals -->
    <barcode-preview-modal
      v-model="showBarcodeModal"
      :product="selectedProduct"
      @print="handlePrint"
    />

    <batch-barcode-print-modal
      v-model="showBatchPrintModal"
      :products="selectedProducts"
      @print="handleBatchPrint"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { myMixin } from "@/mixin/main";
import BarcodePreviewModal from "@/components/BarcodePreviewModal";
import BatchBarcodePrintModal from "@/components/BatchBarcodePrintModal";

export default {
  mixins: [myMixin],
  components: {
    BarcodePreviewModal,
    BatchBarcodePrintModal,
  },
  data() {
    return {
      loading: false,
      search: "",
      viewMode: "list",
      showBarcodeModal: false,
      selectedProduct: null,
      selectedProducts: [],
      showBatchPrintModal: false,
      // Grid view pagination
      gridPage: 1,
      gridItemsPerPage: 20,
      // Table view pagination
      tableOptions: {
        page: 1,
        itemsPerPage: 20,
      },
      tableHeaders: [
        {
          text: this.$t("image") || "Image",
          value: "img",
          sortable: false,
          width: 60,
        },
        {
          text: this.$t("barcode") || "Barcode",
          value: "code",
          width: 130,
        },
        {
          text: this.$t("product_name") || "Product Name",
          value: "name",
        },
        {
          text: this.$t("category") || "Category",
          value: "category.la_name",
          sortable: false,
        },
        {
          text: this.$t("unit") || "Unit",
          value: "size.name",
          sortable: false,
          width: 90,
        },
        {
          text: this.$t("price") || "Price",
          value: "price_sale",
          width: 120,
        },
        {
          text: this.$t("stock") || "Stock",
          value: "stock",
          width: 80,
        },
        {
          text: this.$t("status") || "Status",
          value: "is_active",
          width: 90,
          align: "center",
        },
        {
          text: this.$t("actions") || "Actions",
          value: "actions",
          sortable: false,
          align: "right",
          width: 70,
        },
      ],
    };
  },
  computed: {
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("total_products") || "Total Products",
          icon: "fal fa-barcode",
          value: Number(this.product_barcode.total || 0),
        },
        {
          key: "active",
          label: this.$t("active") || "Active",
          icon: "fal fa-check-circle",
          value: this.activeProductsCount,
        },
        {
          key: "selected",
          label: this.$t("selected") || "Selected",
          icon: "fal fa-print",
          value: this.selectedProducts.length,
        },
      ];
    },
    ...mapGetters({
      user: "auth/user",
      product_barcode: "index/product_barcode",
      rowsPerPageItems: "index/rowsPerPageItems",
    }),
    products() {
      return this.product_barcode.data || [];
    },
    activeProductsCount() {
      return this.products.filter((p) => p.is_active).length;
    },
    paginationStart() {
      const total = this.product_barcode.total || 0;
      if (total === 0) return 0;
      return (this.currentPage - 1) * this.currentItemsPerPage + 1;
    },
    paginationEnd() {
      const total = this.product_barcode.total || 0;
      const end = this.currentPage * this.currentItemsPerPage;
      return end > total ? total : end;
    },
    totalPages() {
      const total = this.product_barcode.total || 0;
      return Math.ceil(total / this.currentItemsPerPage) || 1;
    },
    allVisibleSelected() {
      return (
        this.products.length > 0 &&
        this.products.every((p) => this.isSelected(p))
      );
    },
    someVisibleSelected() {
      return this.products.some((p) => this.isSelected(p));
    },
    currentPage: {
      get() {
        return this.viewMode === "grid" ? this.gridPage : this.tableOptions.page;
      },
      set(val) {
        if (this.viewMode === "grid") {
          this.gridPage = val;
        } else {
          this.tableOptions.page = val;
        }
      },
    },
    currentItemsPerPage: {
      get() {
        return this.viewMode === "grid"
          ? this.gridItemsPerPage
          : this.tableOptions.itemsPerPage;
      },
      set(val) {
        if (this.viewMode === "grid") {
          this.gridItemsPerPage = val;
          this.gridPage = 1;
        } else {
          this.tableOptions.itemsPerPage = val;
          this.tableOptions.page = 1;
        }
        this.fetchProducts();
      },
    },
  },
  watch: {
    gridPage() {
      if (this.viewMode === "grid") {
        this.fetchProducts();
      }
    },
    "tableOptions.page"() {
      if (this.viewMode === "list") {
        this.fetchProducts();
      }
    },
    search() {
      this.gridPage = 1;
      this.tableOptions.page = 1;
    },
    viewMode(newMode) {
      if (newMode === "grid") {
        this.gridPage = 1;
      } else {
        this.tableOptions.page = 1;
      }
      this.fetchProducts();
    },
  },
  created() {
    this.fetchProducts();
  },
  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    getUnitDisplay(product) {
      if (product && product.size && product.size.name) {
        return product.size.name;
      }
      return this.$t("no_unit") || "No Unit";
    },
    async fetchProducts() {
      this.loading = true;
      const page = this.currentPage;
      const perPage = this.currentItemsPerPage;
      const body = {
        that: this,
        url: `products_all?q=${this.search}&per_page=${perPage}&page=${page}`,
        commit: "SET_PRODUCT_BARCODE",
      };
      await this.fetch(body);
      this.loading = false;
    },
    openBarcodeModal(product) {
      this.selectedProduct = product;
      this.showBarcodeModal = true;
    },
    handlePrint({ barcodes }) {
      this.$toast.success(
        this.$t("barcode_printed_successfully") ||
          `Barcode printed successfully! (${barcodes.length} copies)`
      );
    },
    isSelected(product) {
      return this.selectedProducts.some((p) => p.code === product.code);
    },
    toggleSelect(product) {
      const idx = this.selectedProducts.findIndex(
        (p) => p.code === product.code
      );
      if (idx === -1) {
        this.selectedProducts.push(product);
      } else {
        this.selectedProducts.splice(idx, 1);
      }
    },
    toggleSelectAllVisible(checked) {
      if (checked) {
        this.products.forEach((product) => {
          if (!this.isSelected(product)) {
            this.selectedProducts.push(product);
          }
        });
      } else {
        const visibleCodes = new Set(this.products.map((p) => p.code));
        this.selectedProducts = this.selectedProducts.filter(
          (p) => !visibleCodes.has(p.code)
        );
      }
    },
    clearSelection() {
      this.selectedProducts = [];
    },
    openBatchPrintModal() {
      if (this.selectedProducts.length === 0) return;
      this.showBatchPrintModal = true;
    },
    handleBatchPrint({ totalLabels }) {
      this.$toast.success(
        this.$t("barcode_printed_successfully") ||
          `Barcodes printed successfully! (${totalLabels} labels)`
      );
    },
    async copyBarcode(code) {
      if (!code || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(String(code));
        this.$toast.success(
          this.$t("copied_to_clipboard") || "Copied to clipboard"
        );
      } catch (err) {
        console.error(err);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        if (this.viewMode === "grid") {
          this.gridPage--;
        } else {
          this.tableOptions.page--;
        }
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        if (this.viewMode === "grid") {
          this.gridPage++;
        } else {
          this.tableOptions.page++;
        }
      }
    },
  },
};
</script>

<style scoped>
/* Page Variables matching /products */
.ps-barcode-page {
  --ps-bc-ink: #141021;
  --ps-bc-muted: #64748b;
  --ps-bc-faint: #94a3b8;
  --ps-bc-accent: var(--v-primary-base, #5b39c9);
  --ps-bc-card-bg: #ffffff;
  --ps-bc-card-border: #e2e8f0;
  --ps-bc-line: #eef0f6;
  --ps-bc-line-strong: #e2e8f0;
  --ps-bc-track: #eef0f6;
  --ps-bc-row-hover: rgba(91, 57, 201, 0.04);
  --ps-bc-thumb-bg: #f8fafc;
  --ps-bc-control-bg: #ffffff;
}

.theme--dark .ps-barcode-page {
  --ps-bc-ink: #eef0fa;
  --ps-bc-muted: #9aa1bb;
  --ps-bc-faint: #7c82a0;
  --ps-bc-accent: var(--v-primary-base, #9a8cf0);
  --ps-bc-card-bg: #1b1d30;
  --ps-bc-card-border: rgba(154, 140, 240, 0.16);
  --ps-bc-line: rgba(148, 154, 184, 0.12);
  --ps-bc-line-strong: rgba(154, 140, 240, 0.22);
  --ps-bc-track: rgba(154, 140, 240, 0.14);
  --ps-bc-row-hover: rgba(154, 140, 240, 0.08);
  --ps-bc-thumb-bg: rgba(154, 140, 240, 0.08);
  --ps-bc-control-bg: #22243a;
}

/* Checked styling: ALWAYS primary color */
::v-deep .v-input--checkbox.v-input--is-label-active .v-icon,
::v-deep .v-simple-checkbox--selected .v-icon,
::v-deep .v-simple-checkbox.v-simple-checkbox--indeterminate .v-icon,
::v-deep .v-data-table__checkbox .v-icon.mdi-checkbox-marked,
::v-deep .v-data-table__checkbox .v-icon.mdi-minus-box {
  color: var(--v-primary-base) !important;
}

/* Card Selection Highlighting with primary color */
.ps-barcode-card.is-selected {
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 0 0 2px var(--v-primary-base) !important;
  background: rgba(91, 57, 201, 0.04) !important;
}

.theme--dark .ps-barcode-card.is-selected {
  background: rgba(154, 140, 240, 0.1) !important;
}

/* Table Selected Row Highlighting with primary color */
::v-deep .ps-data-table tbody tr.v-data-table__selected {
  background-color: rgba(91, 57, 201, 0.08) !important;
}

::v-deep .ps-data-table tbody tr.v-data-table__selected:hover {
  background-color: rgba(91, 57, 201, 0.12) !important;
}

.theme--dark ::v-deep .ps-data-table tbody tr.v-data-table__selected {
  background-color: rgba(154, 140, 240, 0.15) !important;
}

/* KPI Row (Matches /report-bill) */

.ps-card-soft {
  background: var(--ps-bc-card-bg) !important;
  border: 1px solid var(--ps-bc-card-border) !important;
  border-radius: 16px !important;
  box-shadow: none !important;
}

/* Main Table Card */
.ps-table-card {
  border-radius: 18px !important;
}

.ps-table-head {
  gap: 10px;
  margin-bottom: 14px;
}

.ps-title-tick {
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: var(--v-primary-base);
  display: inline-block;
  margin-right: 8px;
}

.ps-table-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: var(--ps-bc-ink);
}

.ps-count-note {
  margin-left: 8px;
  font-size: 12px;
  color: var(--ps-bc-faint);
  font-weight: 500;
}

.ps-status-toggle {
  border: 1px solid #e2e8f0;
  border-radius: 10px !important;
  background: #f1f2f7 !important;
  padding: 3px;
  height: 38px !important;
  gap: 4px;
  display: inline-flex;
  align-items: center;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn {
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background-color: transparent !important;
  border: none !important;
  height: 30px !important;
  padding: 0 12px;
  border-radius: 7px !important;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn.v-btn--active {
  background-color: #ffffff !important;
  color: #5b39c9 !important;
  font-weight: 700 !important;
  border-radius: 7px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}

.ps-barcode--dark .ps-status-toggle {
  border-color: rgba(154, 140, 240, 0.16);
  background: #1b1d30 !important;
}

.ps-barcode--dark .ps-status-toggle .v-btn {
  color: #7c82a0;
}

.ps-barcode--dark .ps-status-toggle .v-btn.v-btn--active {
  background-color: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

/* Filter Bar */
.ps-filter-bar {
  gap: 12px;
  margin-bottom: 14px;
}

.ps-search-wrap {
  flex: 1 1 260px;
  max-width: 380px;
  min-width: 200px;
}

.ps-select-all-cb {
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-bc-ink);
}

.ps-batch-print-btn {
  text-transform: none;
  font-weight: 700;
  font-size: 13px;
  border-radius: 10px !important;
}

.ps-print-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 999px;
  font-size: 11px;
  padding: 1px 7px;
  font-weight: 800;
}

/* Selection Banner */
.ps-selection-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  margin-bottom: 14px;
  border-radius: 12px;
  background: rgba(91, 57, 201, 0.08);
  border: 1px solid rgba(91, 57, 201, 0.25);
  border-left: 4px solid var(--v-primary-base);
}

.theme--dark .ps-selection-banner {
  background: rgba(154, 140, 240, 0.12);
  border-color: rgba(154, 140, 240, 0.35);
  border-left: 4px solid var(--v-primary-base);
}

.ps-selection-banner-text {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ps-bc-ink);
}

.ps-banner-clear-btn {
  text-transform: none;
  font-weight: 700;
}

.ps-banner-print-btn {
  text-transform: none;
  font-weight: 700;
  border-radius: 8px;
}

/* Grid Layout */
.ps-barcode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 14px;
}

.ps-barcode-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--ps-bc-card-bg);
  border: 1.5px solid var(--ps-bc-card-border);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.ps-barcode-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  border-color: rgba(91, 57, 201, 0.4);
}

.ps-card-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 4;
}

.ps-card-image-wrap {
  position: relative;
  height: 160px;
  background: var(--ps-bc-thumb-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid var(--ps-bc-line);
}

.ps-card-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.ps-card-image ::v-deep img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.ps-barcode-card:hover .ps-card-image ::v-deep img {
  transform: scale(1.05);
}

.ps-card-status-chip {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: rgba(220, 38, 38, 0.12);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.25);
}

.ps-card-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(20, 16, 33, 0.65);
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
  cursor: pointer;
}

.ps-card-image-wrap:hover .ps-card-overlay {
  opacity: 1;
}

.ps-overlay-btn {
  margin-bottom: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.ps-overlay-text {
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ps-card-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.ps-card-name {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ps-bc-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-card-code {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: var(--ps-bc-faint);
  font-variant-numeric: tabular-nums;
  gap: 4px;
}

.ps-copy-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border: 0;
  background: transparent;
  color: var(--ps-bc-faint);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.ps-copy-code:hover {
  color: var(--v-primary-base);
  background: rgba(91, 57, 201, 0.1);
}

.ps-card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.ps-category-pill,
.ps-unit-pill {
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  color: var(--ps-bc-muted);
  background: var(--ps-bc-track);
  border: 1px solid var(--ps-bc-card-border);
  white-space: nowrap;
}

.ps-card-footer {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid var(--ps-bc-line);
}

.ps-card-price {
  font-size: 15px;
  font-weight: 800;
  color: var(--v-primary-base);
  font-variant-numeric: tabular-nums;
}

.ps-card-action-icon {
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.ps-card-action-icon:hover {
  background: rgba(91, 57, 201, 0.1);
}

/* List Table Styling matching /products */
.ps-table-wrap {
  overflow-x: auto;
}

.ps-data-table {
  background: transparent !important;
}

::v-deep .ps-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: var(--ps-bc-faint) !important;
  border-bottom: 1px solid var(--ps-bc-line-strong) !important;
  padding: 10px 14px !important;
  white-space: nowrap;
}

::v-deep .ps-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 10px 14px !important;
  border-bottom: 1px solid var(--ps-bc-line) !important;
  font-size: 13px !important;
  color: var(--ps-bc-ink) !important;
}

::v-deep .ps-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: var(--ps-bc-row-hover) !important;
}

.ps-thumb-avatar {
  background: var(--ps-bc-thumb-bg) !important;
  border: 1px solid var(--ps-bc-card-border);
}

.ps-row-code {
  font-size: 12px;
  color: var(--ps-bc-faint);
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
}

.ps-product-name {
  font-size: 13.5px;
  color: var(--ps-bc-ink);
}

.ps-price-cell {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--v-primary-base);
  font-variant-numeric: tabular-nums;
}

.ps-status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.ps-status-chip.is-active {
  background: rgba(0, 200, 83, 0.12);
  color: #00c853;
  border: 1px solid rgba(0, 200, 83, 0.25);
}

.ps-status-chip.is-inactive {
  background: rgba(220, 38, 38, 0.12);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.25);
}

.ps-row-action-btn {
  border-radius: 8px;
}
.ps-row-action-btn:hover {
  background: rgba(91, 57, 201, 0.1);
}

/* Pagination matching /products */
.ps-pagination {
  gap: 12px;
  border-top: 1px solid var(--ps-bc-line);
}

.ps-page-note {
  font-size: 12px;
  font-weight: 600;
  color: var(--ps-bc-faint);
}

.ps-per-page {
  gap: 8px;
  font-size: 12px;
  color: var(--ps-bc-muted);
}

.ps-per-page-select select {
  font-size: 12px;
  font-weight: 600;
  color: var(--ps-bc-ink);
  background: var(--ps-bc-control-bg);
  border: 1px solid var(--ps-bc-card-border);
  border-radius: 8px;
  padding: 4px 8px;
  outline: none;
  cursor: pointer;
}

.ps-pager {
  gap: 6px;
}

.ps-pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--ps-bc-control-bg);
  border: 1px solid var(--ps-bc-card-border);
  color: var(--ps-bc-ink);
  cursor: pointer;
  transition: all 0.15s ease;
}

.ps-pager-btn:hover:not(.is-disabled) {
  border-color: var(--v-primary-base);
  color: var(--v-primary-base);
}

.ps-pager-btn.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ps-pager-info {
  font-size: 12px;
  font-weight: 600;
  color: var(--ps-bc-muted);
  padding: 0 6px;
  font-variant-numeric: tabular-nums;
}

/* Empty State */
.ps-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
</style>
